// Shared audio analysis utilities for waveform generation

export interface AudioAnalysisResult {
  waveformData: number[];
  success: boolean;
  error?: string;
  cacheKey?: string;
}

interface CachedWaveform {
  waveformData: number[];
  timestamp: number;
  segments: number;
}

export class AudioAnalyzer {
  private static audioContextRef: AudioContext | null = null;
  private static CACHE_PREFIX = 'audio_waveform_';
  private static CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

  // Get or create audio context
  private static getAudioContext(): AudioContext | null {
    try {
      if (!this.audioContextRef || this.audioContextRef.state === 'closed') {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
          return null;
        }
        this.audioContextRef = new AudioContextClass();
      }
      return this.audioContextRef;
    } catch (error) {
      console.error('Failed to create audio context:', error);
      return null;
    }
  }

  // Generate fallback waveform
  static generateFallbackWaveform(segments: number = 50): number[] {
    return Array.from({ length: segments }, () => 0.15);
  }

  // Generate simple realistic waveform
  static generateSimpleWaveform(segments: number = 50): number[] {
    const waveform = [];
    for (let i = 0; i < segments; i++) {
      const baseHeight = 0.15;
      const variation = Math.sin(i * 0.3) * 0.1 + Math.random() * 0.15;
      const height = Math.max(baseHeight, Math.min(baseHeight + variation, 0.6));
      waveform.push(height);
    }
    return waveform;
  }

  // Generate cache key from URL
  static generateCacheKey(url: string): string {
    // Extract the filename or unique part from the URL
    const urlParts = url.split('/');
    const filenameWithParams = urlParts[urlParts.length - 1];
    const filename = filenameWithParams.split('?')[0];
    
    // Use the filename or full URL hash if needed
    return this.CACHE_PREFIX + filename;
  }

  // Store waveform data in cache
  static cacheWaveform(cacheKey: string, waveformData: number[], segments: number): void {
    try {
      const cacheData: CachedWaveform = {
        waveformData,
        timestamp: Date.now(),
        segments
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache waveform data:', error);
    }
  }

  // Get cached waveform data
  static getCachedWaveform(cacheKey: string, segments: number): number[] | null {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (!cachedData) return null;
      
      const parsedData: CachedWaveform = JSON.parse(cachedData);
      
      // Check if cache is expired or segments don't match
      if (
        Date.now() - parsedData.timestamp > this.CACHE_EXPIRY ||
        parsedData.segments !== segments
      ) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return parsedData.waveformData;
    } catch (error) {
      console.warn('Failed to retrieve cached waveform:', error);
      return null;
    }
  }

  // Main audio analysis function
  static async analyzeAudio(
    audioUrl: string, 
    segments: number = 50,
    scaleFactor: number = 12
  ): Promise<AudioAnalysisResult> {
    try {
      // Generate cache key for this audio URL
      const cacheKey = this.generateCacheKey(audioUrl);
      
      // Check cache first
      const cachedWaveform = this.getCachedWaveform(cacheKey, segments);
      if (cachedWaveform) {
        return {
          waveformData: cachedWaveform,
          success: true,
          cacheKey
        };
      }
      
      // Check if Web Audio API is available
      const audioContext = this.getAudioContext();
      if (!audioContext) {
        return {
          waveformData: this.generateSimpleWaveform(segments),
          success: false,
          error: 'Web Audio API not supported'
        };
      }

      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Fetch audio file
      let arrayBuffer: ArrayBuffer;
      try {
        const response = await fetch(audioUrl, {
          mode: 'cors',
          credentials: 'omit'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        arrayBuffer = await response.arrayBuffer();
      } catch (fetchError) {
        return {
          waveformData: this.generateSimpleWaveform(segments),
          success: false,
          error: 'Failed to fetch audio file'
        };
      }

      // Decode audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Get audio data from first channel
      const channelData = audioBuffer.getChannelData(0);
      const samples = channelData.length;
      const samplesPerSegment = Math.floor(samples / segments);
      
      const peaks: number[] = [];
      
      // Calculate RMS for each segment
      for (let i = 0; i < segments; i++) {
        const start = i * samplesPerSegment;
        const end = Math.min(start + samplesPerSegment, samples);
        
        let sum = 0;
        let count = 0;
        
        for (let j = start; j < end; j++) {
          sum += channelData[j] * channelData[j];
          count++;
        }
        
        const rms = Math.sqrt(sum / count);
        let normalizedHeight = Math.min(rms * scaleFactor, 1);
        normalizedHeight = Math.max(normalizedHeight, 0.08);
        
        peaks.push(normalizedHeight);
      }
      
      // Cache the waveform data
      this.cacheWaveform(cacheKey, peaks, segments);
      
      return {
        waveformData: peaks,
        success: true,
        cacheKey
      };
      
    } catch (error) {
      return {
        waveformData: this.generateSimpleWaveform(segments),
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Cleanup audio context
  static cleanup(): void {
    if (this.audioContextRef) {
      this.audioContextRef.close();
      this.audioContextRef = null;
    }
  }
}
